import { get } from 'lodash';

import { UNAUTHORIZED_HTTP_CODE } from '../service/Error';
import { showModal, TYPES } from './modal';
import { redirectPage } from './screen';
import { setOnlineStatus } from './online';
import { getCurrentFlightInfo } from '../selectors/screen';
import { triggerAttachmentDownload } from '../components/MessengerPage/attachments';

export const UPDATE_MESSENGER_CHATS = 'UPDATE_MESSENGER_CHATS';
export const UPDATE_MESSENGER_THREAD = 'UPDATE_MESSENGER_THREAD';
export const SET_MESSENGER_ACTIVE_CHAT = 'SET_MESSENGER_ACTIVE_CHAT';
export const SET_MESSENGER_STATUS = 'SET_MESSENGER_STATUS';
export const SET_MESSENGER_SEARCH = 'SET_MESSENGER_SEARCH';
export const SET_MESSENGER_FLIGHT_SCOPE = 'SET_MESSENGER_FLIGHT_SCOPE';
export const ENQUEUE_MESSENGER_MESSAGE = 'ENQUEUE_MESSENGER_MESSAGE';
export const RESOLVE_MESSENGER_MESSAGE = 'RESOLVE_MESSENGER_MESSAGE';
export const FAIL_MESSENGER_MESSAGE = 'FAIL_MESSENGER_MESSAGE';
export const MARK_MESSENGER_CHAT_READ = 'MARK_MESSENGER_CHAT_READ';
export const UPDATE_MESSENGER_RECIPIENTS = 'UPDATE_MESSENGER_RECIPIENTS';

const isAllowed = (state) => get(state, 'routesDeclaration.options.allowMessenger', false);
const getUserId = (state) => get(state, 'login.user.id');
const isOnline = (state) => {
    const online = get(state, 'online');

    return typeof online === 'boolean' ? online : get(online, 'status', true);
};

export const setMessengerStatus = (status) => ({ type: SET_MESSENGER_STATUS, status });
export const setMessengerSearch = (search) => ({ type: SET_MESSENGER_SEARCH, search });
export const setMessengerFlightScope = (planFlightId) => ({ type: SET_MESSENGER_FLIGHT_SCOPE, planFlightId });
export const setMessengerActiveChat = (chat) => ({ type: SET_MESSENGER_ACTIVE_CHAT, chat });

/**
 * Загрузка списка чатов (инкрементально по syncTime).
 */
export function loadMessengerChats() {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state) || !isOnline(state)) {
            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve();
        }

        const syncTime = get(state, 'messenger.syncTime');

        dispatch(setMessengerStatus('loading'));

        return apiManager
            .getMessengerChats(userId, syncTime)
            .then((res) => {
                dispatch({ type: UPDATE_MESSENGER_CHATS, chats: (res && res.data) || [], syncTime: res && res.syncTime, replace: true });
                dispatch(setMessengerStatus('idle'));
            })
            .catch((err) => {
                dispatch(setMessengerStatus('error'));
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }
            });
    };
}

/**
 * Загрузка истории конкретного чата (групповой / личный).
 */
export function loadMessengerThread(chat) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state) || !chat) {
            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId || !isOnline(state)) {
            return Promise.resolve();
        }

        const syncTime = get(state, `messenger.threadSyncTime.${chat.type}_${chat.id}`);

        const request =
            chat.type === 'personal'
                ? apiManager.getMessengerPersonal(userId, chat.interlocutorId || chat.id, syncTime)
                : apiManager.getMessengerForum(userId, chat.id, syncTime);

        dispatch(setMessengerStatus('loading'));

        return request
            .then((res) => {
                dispatch({
                    type: UPDATE_MESSENGER_THREAD,
                    chat,
                    messages: (res && res.data) || [],
                    syncTime: res && res.syncTime,
                });
                // Групповой чат: открытие ветки отмечает входящие прочитанными на бэкенде —
                // сбрасываем счётчик в списке и в боковом меню без ожидания sync списка чатов.
                if (chat.type === 'forum') {
                    dispatch(markMessengerChatRead(chat));
                }
                dispatch(setMessengerStatus('idle'));
            })
            .catch((err) => {
                dispatch(setMessengerStatus('error'));
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }
            });
    };
}

/**
 * Чат рейса: открыть/создать тему форума по полётному заданию и перейти в раздел.
 * Вызывается из бокового меню при активном ПЗ (planFlightId = текущий currentLeg).
 * Офлайн/без рейса — просто открываем раздел «Сообщения», тема подтянется при сети.
 */
export function openFlightChat(planFlightId) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state)) {
            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve();
        }

        const flightInfo = getCurrentFlightInfo(state) || {};
        const titleParts = [flightInfo.flightNumber, flightInfo.route].filter(Boolean).join(' ');
        const fallbackTitle = [titleParts, flightInfo.date && `(${flightInfo.date})`].filter(Boolean).join(' ') || undefined;

        dispatch(redirectPage('messages'));

        if (!planFlightId || !isOnline(state)) {
            if (planFlightId) {
                dispatch(setMessengerFlightScope(planFlightId));
            }

            return Promise.resolve();
        }

        dispatch(setMessengerFlightScope(planFlightId));
        dispatch(setMessengerStatus('loading'));

        return apiManager
            .getMessengerFlightChat(userId, planFlightId)
            .then((res) => {
                const data = res && res.data;

                if (!data || !data.themeId) {
                    dispatch(setMessengerStatus('idle'));

                    return;
                }

                const chat = {
                    id: data.themeId,
                    type: 'forum',
                    title: data.title || fallbackTitle,
                    planFlightId,
                    isFlight: true,
                    isGroup: true,
                };

                dispatch({ type: UPDATE_MESSENGER_CHATS, chats: [chat] });
                dispatch(setMessengerActiveChat(chat));
                dispatch(setMessengerStatus('idle'));
                dispatch(loadMessengerThread(chat));
                dispatch(markMessengerChatRead(chat));
            })
            .catch((err) => {
                dispatch(setMessengerStatus('error'));
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }
            });
    };
}

/**
 * Общий мессенджер: полный список чатов (вне контекста текущего рейса/ПЗ).
 */
export function openMessengerInbox() {
    return (dispatch) => {
        dispatch(setMessengerFlightScope(null));
        dispatch(setMessengerActiveChat(null));
        dispatch(redirectPage('messages'));
        dispatch(loadMessengerChats());
    };
}

/**
 * Загрузка списка адресатов для нового личного диалога.
 */
export function loadMessengerRecipients() {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state) || !isOnline(state)) {
            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve();
        }

        return apiManager
            .getMessengerRecipients(userId)
            .then((res) => {
                dispatch({ type: UPDATE_MESSENGER_RECIPIENTS, recipients: (res && res.data) || [] });
            })
            .catch((err) => {
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }
            });
    };
}

/**
 * Старт нового личного диалога с выбранным адресатом: делаем чат активным
 * и подгружаем историю (если переписка уже была). Сам чат появится в списке
 * после первого отправленного сообщения и ближайшей синхронизации.
 */
export function startPersonalChat(recipient) {
    return (dispatch, getState) => {
        const state = getState();

        if (!isAllowed(state) || !recipient || !recipient.id) {
            return Promise.resolve();
        }

        const chat = {
            id: recipient.id,
            type: 'personal',
            title: recipient.name,
            interlocutorId: recipient.id,
        };

        dispatch(setMessengerActiveChat(chat));
        dispatch(loadMessengerThread(chat));

        return Promise.resolve(chat);
    };
}

/**
 * Создание группового чата вручную (тема форума без привязки к рейсу).
 * Требует сети: тема создаётся в IS на сервере. После успеха добавляем чат
 * в список, делаем активным и открываем. Возвращает созданный чат или undefined.
 */
export function createMessengerGroupChat(name, memberIds) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();
        const title = (name || '').trim();

        if (!isAllowed(state) || !isOnline(state) || !title) {
            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve();
        }

        return apiManager
            .createMessengerGroup(userId, title, memberIds || [])
            .then((res) => {
                if (res && res.ok && res.chat && res.chat.id != null) {
                    dispatch({ type: UPDATE_MESSENGER_CHATS, chats: [res.chat] });
                    dispatch(setMessengerActiveChat(res.chat));
                    dispatch(loadMessengerThread(res.chat));

                    return res.chat;
                }

                dispatch(setMessengerStatus('error'));

                return undefined;
            })
            .catch((err) => {
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }

                return undefined;
            });
    };
}

/**
 * Загрузить состав группы (для экрана участников). Возвращает массив [{id,name,isOwner}].
 */
export function loadMessengerGroupMembers(chat) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state) || !isOnline(state) || !chat || !chat.id) {
            return Promise.resolve([]);
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve([]);
        }

        return apiManager
            .getMessengerGroupMembers(userId, chat.id)
            .then((res) => (res && res.data) || [])
            .catch((err) => {
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }

                return [];
            });
    };
}

/**
 * Добавить участников в группу. Возвращает обновлённый список участников или null.
 * После успеха обновляем список чатов (изменился состав/счётчик).
 */
export function addMessengerGroupMembers(chat, memberIds) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();
        const ids = (memberIds || []).filter((id) => id != null);

        if (!isAllowed(state) || !isOnline(state) || !chat || !chat.id || !ids.length) {
            return Promise.resolve(null);
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve(null);
        }

        return apiManager
            .addMessengerGroupMembers(userId, chat.id, ids)
            .then((res) => {
                if (res && res.ok) {
                    dispatch(loadMessengerChats());

                    return res.members || [];
                }

                return null;
            })
            .catch((err) => {
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }

                return null;
            });
    };
}

/**
 * Отправка сообщения. Всегда сначала кладём в очередь (outbox), затем пытаемся
 * отправить. При отсутствии связи сообщение остаётся в очереди и будет
 * доотправлено автоматически при появлении сети (flushMessengerOutbox).
 * Сообщение может содержать текст и/или вложения (фото/файлы).
 */
export function sendMessengerMessage(chat, text, attachments) {
    return (dispatch, getState) => {
        const state = getState();

        const trimmed = (text || '').trim();
        const list = attachments || [];

        if (!isAllowed(state) || !chat || (!trimmed && list.length === 0)) {
            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve();
        }

        const queued = {
            localId: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            chatType: chat.type,
            chatId: chat.type === 'personal' ? chat.interlocutorId || chat.id : chat.id,
            chatKey: `${chat.type}_${chat.id}`,
            text: trimmed,
            attachments: list,
            createdAt: Date.now(),
            status: 'queued',
        };

        dispatch({ type: ENQUEUE_MESSENGER_MESSAGE, message: queued });

        return dispatch(flushMessengerOutbox());
    };
}

/*
 * Кеш данных вложений по id (id → Promise<{data,contentType,name,...}|null>).
 * Хранит промис, чтобы дедуплицировать параллельные/повторные запросы (лента
 * перерисовывается при поллинге, а ссылка на объект вложения каждый раз новая).
 */
const attachmentDataCache = new Map();

/**
 * Лениво подгрузить содержимое вложения (для предпросмотра фото в ленте).
 * Локальные (ещё не отправленные) вложения уже содержат данные. Возвращает объект
 * вложения с полем data (base64) или null. Результат кешируется по id.
 */
export function fetchMessengerAttachmentData(attachment) {
    return (dispatch, getState, { apiManager }) => {
        if (!attachment) {
            return Promise.resolve(null);
        }

        if (attachment.data || attachment.preview) {
            return Promise.resolve(attachment);
        }

        const id = attachment.id;
        const state = getState();

        if (id == null || !isAllowed(state) || !isOnline(state)) {
            return Promise.resolve(null);
        }

        if (attachmentDataCache.has(id)) {
            return attachmentDataCache.get(id);
        }

        const userId = getUserId(state);

        if (!userId) {
            return Promise.resolve(null);
        }

        const request = apiManager
            .getMessengerAttachment(userId, id)
            .then((res) => (res && res.data) || null)
            .catch((err) => {
                attachmentDataCache.delete(id); // даём шанс на повторную попытку позже
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }

                return null;
            });

        attachmentDataCache.set(id, request);

        return request;
    };
}

/**
 * Открыть/скачать вложение. Локальные (ещё не отправленные) вложения уже содержат
 * данные — открываем сразу. Для серверных вложений тянем содержимое по id.
 */
export function openMessengerAttachment(attachment) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state) || !attachment) {
            return Promise.resolve();
        }

        if (attachment.data || attachment.preview) {
            triggerAttachmentDownload(attachment);

            return Promise.resolve();
        }

        const userId = getUserId(state);

        if (!userId || !attachment.id || !isOnline(state)) {
            return Promise.resolve();
        }

        return apiManager
            .getMessengerAttachment(userId, attachment.id)
            .then((res) => {
                const data = res && res.data;

                if (data && data.data) {
                    triggerAttachmentDownload(data);
                }
            })
            .catch((err) => {
                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                    dispatch(showModal(TYPES.RELOGIN, {}));
                }
            });
    };
}

/**
 * Отправка всех накопленных сообщений из очереди. Вызывается:
 *  - сразу после постановки сообщения в очередь;
 *  - автоматически при появлении сети (MessengerSyncManager).
 *
 * force=true — попытаться отправить, даже если приложение СЧИТАЕТ себя офлайн.
 * Реальный HTTP-запрос служит «зондом» сети: событие window online/offline в
 * мобильном webview ненадёжно и флаг online может «залипнуть» в false, из-за чего
 * очередь никогда не досылалась. При успешном ответе восстанавливаем флаг online.
 *
 * ВАЖНО: синхронизация запускается из нескольких источников (поллинг 12 c, focus,
 * visibilitychange, восстановление сети), которые могут сработать почти одновременно.
 * Без сериализации несколько flush параллельно снимают один и тот же снапшот очереди
 * и каждое сообщение уходит в IS дважды → дубли в переписке. Поэтому flush выполняется
 * строго по одному: параллельный вызов ждёт уже идущий и возвращает его промис.
 */
let flushInFlight = null;

export function flushMessengerOutbox(force = false) {
    return (dispatch, getState, { apiManager }) => {
        // Уже идёт flush — не запускаем второй, отдаём тот же промис (единая сериализация).
        if (flushInFlight) {
            return flushInFlight;
        }

        const run = () => {
            const state = getState();

            if (!isAllowed(state) || (!force && !isOnline(state))) {
                return Promise.resolve();
            }

            const userId = getUserId(state);
            const outbox = get(state, 'messenger.outbox', []);

            if (!userId || outbox.length === 0) {
                return Promise.resolve();
            }

            // Отправляем последовательно, чтобы сохранить порядок и не залить IS пачкой.
            // Перед каждой отправкой перепроверяем, что сообщение всё ещё в очереди:
            // это страховка от повторной отправки, если защиту от параллельности обошли.
            return outbox.reduce(
                (chain, item) =>
                    chain.then(() => {
                        const stillQueued = get(getState(), 'messenger.outbox', []).some((m) => m.localId === item.localId);

                        if (!stillQueued) {
                            return Promise.resolve();
                        }

                        return apiManager
                            .sendMessengerMessage(userId, item.chatType, item.chatId, item.text, item.attachments)
                            .then((res) => {
                                if (res && res.ok) {
                                    // Успешный ответ = сеть есть: снимаем «залипший» офлайн-флаг.
                                    if (!isOnline(getState())) {
                                        dispatch(setOnlineStatus(true));
                                    }

                                    dispatch({
                                        type: RESOLVE_MESSENGER_MESSAGE,
                                        localId: item.localId,
                                        message: res.message,
                                        chatKey: item.chatKey,
                                    });
                                } else if (res && res.ok === false) {
                                    // Сервер ОТКЛОНИЛ сообщение (не сетевая ошибка): помечаем как
                                    // «не отправлено» и убираем из очереди, чтобы не висели «часы»
                                    // и не было бесконечных повторов на заведомо отклонённой отправке.
                                    dispatch({
                                        type: FAIL_MESSENGER_MESSAGE,
                                        localId: item.localId,
                                        chatKey: item.chatKey,
                                    });
                                    dispatch(setMessengerStatus('error'));
                                }
                            })
                            .catch((err) => {
                                // Сетевая ошибка — оставляем в очереди, попробуем позже.
                                if (err.errorCode === UNAUTHORIZED_HTTP_CODE) {
                                    dispatch(showModal(TYPES.RELOGIN, {}));
                                }
                            });
                    }),
                Promise.resolve()
            );
        };

        flushInFlight = Promise.resolve()
            .then(run)
            .catch(() => {})
            .then(() => {
                flushInFlight = null;
            });

        return flushInFlight;
    };
}

/**
 * Отметка чата прочитанным (сбрасывает счётчик непрочитанных локально и в IS).
 */
export function markMessengerChatRead(chat) {
    return (dispatch, getState, { apiManager }) => {
        const state = getState();

        if (!isAllowed(state) || !chat) {
            return Promise.resolve();
        }

        dispatch({ type: MARK_MESSENGER_CHAT_READ, chatId: chat.id, chatType: chat.type });

        const userId = getUserId(state);

        if (!userId || !isOnline(state) || !chat.lastMessageId) {
            return Promise.resolve();
        }

        return apiManager.markMessengerRead(userId, chat.lastMessageId).catch(() => {
            // Некритично: при следующей синхронизации статус подтянется.
        });
    };
}

/**
 * Полная синхронизация мессенджера: доотправка очереди + обновление списка чатов.
 * Безопасна к частым вызовам (no-op без флага/сети/пользователя).
 */
export function syncMessenger() {
    // force=true: досылаем очередь как сетевой «зонд» даже при залипшем офлайн-флаге.
    return (dispatch) => dispatch(flushMessengerOutbox(true)).then(() => dispatch(loadMessengerChats()));
}
